import { useSyncExternalStore } from "react";

export type LiveState = "connecting" | "live" | "offline";
export interface ConnectionState {
  /** Browser reports a network connection. */
  online: boolean;
  /** Last request to the Desk service failed with a network error. */
  unreachable: boolean;
  /** Server-sent events stream state. */
  live: LiveState;
}

let state: ConnectionState = {
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  unreachable: false,
  live: "connecting",
};
const listeners = new Set<() => void>();

export const connection = {
  get: () => state,
  set(patch: Partial<ConnectionState>) {
    const next = { ...state, ...patch };
    if (next.online === state.online && next.unreachable === state.unreachable && next.live === state.live) return;
    state = next;
    listeners.forEach((l) => l());
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => connection.set({ online: true }));
  window.addEventListener("offline", () => connection.set({ online: false }));
}

export function useConnection(): ConnectionState {
  return useSyncExternalStore(connection.subscribe, connection.get, connection.get);
}

export function isOffline(s: ConnectionState = state): boolean {
  return !s.online || s.unreachable;
}
